export const BMD_COPY = Object.freeze({
  TITLE: "Diagnostics",
  SUBTITLE:
    "Pick a year level and select the topics to assess. The student will answer multiple-choice questions at the chosen year level, with follow-up prompts after each answer.",
  RULES_HEADING: "How diagnostics work",
  YEAR_SELECTOR_LABEL: "Diagnostics year level",
  YEAR_SELECTOR_PLACEHOLDER: "Select a diagnostics year level",
  // Sits under the year select. The rule is easy to get backwards, and getting
  // it backwards means testing a child on a year they have not been taught.
  YEAR_SELECTOR_HINT:
    "Students sit the year they have just completed. A Year 5 student takes Year 4.",
  TIME_BUDGET_LABEL: "Time allowance for this test",
  TIME_BUDGET_HINT:
    "A guideline. You'll be warned if the test runs over, but can still proceed.",
  OVER_BUDGET_TITLE: "Over your time budget",
  OVER_BUDGET_CONFIRM: "Proceed anyway",
  OVER_BUDGET_CANCEL: "Cancel",
  PREVIEW_BUTTON: "Preview",
  PREVIEW_LOADING: "Loading…",
  PREVIEW_MODAL_TITLE: "Diagnostics Preview",
  PREVIEW_EMPTY:
    "No diagnostic questions were found for this year level or topic selection.",
  EMPTY_YEAR_LEVELS:
    "No diagnostic year levels available for this student and subject.",
})

// Tutor-selectable time budgets for a single BMD test. null minutes = no limit.
export type BmdTimeBudgetOption = { label: string; minutes: number | null }

// Labels are ranges; `minutes` is the top of each range, and that is what the
// over-budget check compares against.
export const BMD_TIME_BUDGET_OPTIONS: readonly BmdTimeBudgetOption[] =
  Object.freeze([
    { label: "1 to 15 minutes", minutes: 15 },
    { label: "16 to 30 minutes", minutes: 30 },
    { label: "31 to 45 minutes", minutes: 45 },
    { label: "46 to 60 minutes", minutes: 60 },
    { label: "61 to 90 minutes", minutes: 90 },
    { label: "No limit", minutes: null },
  ])

export const BMD_DEFAULT_TIME_BUDGET_MINUTES = 30

// When true, the assignment tab uses the cluster picker: clusters ordered by
// strand.order, sequential unlock, and selecting a cluster pulls in all of its
// unlocked topics. When false, the tab renders the year/topics flow instead.
// Set to false to roll back the cluster UI without touching the data path.
export const BMD_STRAND_SELECTION_ENABLED = true

// On the prerequisite skill tree, a skill the chain never reached is drawn blue
// rather than orange: it went untested because the skill above it was answered
// correctly, not because anything is still owed on it.
//
// When true, that reasoning carries all the way down - a skill the chain never
// reached is also a skill whose own prerequisites it never reached, so the whole
// column below turns blue. When false, only the skills sitting directly under a
// correct answer do, and everything below them stays orange.
//
// Flip to false to go back to first-level-only. Nothing else changes: the seeds,
// the colour and the legend are identical either way.
export const BMD_CHAIN_STOPPED_PROPAGATES: boolean = true

export const BMD_RULES: readonly string[] = Object.freeze([
  "Pick the year level to assess at. Students take the diagnostic one year below their current year. A Year 5 student takes the Year 4 test.",
  "Select one or more clusters. The student completes every question in a cluster before moving on to the next.",
  "Set a time allowance for the test. It's a guideline, not a cut-off. You can proceed past it after confirming.",
  "Each question is multiple choice: one correct answer and three distractors, each written to catch a different mistake: a fundamental misconception, a lack of depth, or confusion with a recently learnt skill.",
  "Students get 60 seconds per question. Up to Year 3 that covers the answer and a confidence rating. From Year 4 they can also type why they chose their option. That is optional, and written within the same 60 seconds.",
  "Skipped and timed-out questions return at the end of the test. No feedback is shown to the student while the test is running.",
  "If a student selects the fundamental misconception, a similar question on the same skill is added to the end of the test. Select it again and the skill is flagged in the report as needing your attention.",
  "If a student selects lack of depth or confusion with a recently learnt skill, you can assign a prerequisite test. It works down through the prerequisite skills, as far as it needs to go, until it finds the exact skill they don't understand.",
  "Every question answered wrong produces a Skill Builder: an interactive worksheet pairing a worked example with a question for the student to try. Skill Builders are graded automatically, and any scored under 75% is flagged for you.",
  "Students can pause and resume at any point, including as homework. Their position, answers and timer state are saved.",
  "You can add more clusters to an in-progress diagnostic at any time; new clusters queue at the end of the student's remaining work.",
])
